/**
 * <ActivityWidthToggle> — au grand écran, la visio ouverte : le bouton rond posé sur la séparation entre
 * la visio et l'activité, qui agrandit l'activité ou la rend à son volet (Wajih, daily du 23/10, l'option
 * (b) du cadrage du 22/10). Deux tailles, pas de poignée à glisser (`liveColumns.activityPaneWidth`).
 *
 * Le chevron montre où va la séparation : vers la gauche, l'activité s'agrandit ; vers la droite, elle se
 * réduit. C'est un `<IconButton>` : il se tabule, Entrée ou Espace le pressent, et le focus porte l'anneau
 * jaune du livre ; rond comme les boutons de la scène (`STAGE_BUTTON`), sa zone de toucher atteint 44 px.
 *
 * Il se pose lui-même à cheval sur le bord gauche de ce qui le porte (le volet de l'activité), à
 * mi-hauteur. La page décide quand il est offert.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { STAGE_BUTTON } from "../../organisms/LiveStage/stageLayout"
import { shadowStyle } from "../../utils/shadow"
import { slopTo } from "../../utils/touchTarget"
import { type UILiveToggle } from "../../../ports/primary/ui/types/studentLive"
import { ACTIVITY_WIDTH_TOGGLE_FACE as FACE } from "./activityWidthToggleFace"

/** Son contour d'un pixel compris : c'est lui qu'on centre sur la séparation. */
const EDGE = 1
const OUTER = STAGE_BUTTON + 2 * EDGE

const TEXTS = {
    enlarge: "Agrandir l'activité",
    restore: "Réduire l'activité",
}

/** `on` : l'activité est agrandie. */
export function ActivityWidthToggle(props: UILiveToggle) {
    const { on, onToggle } = props
    return (
        <View style={styles.slot} pointerEvents="box-none">
            <View style={styles.button}>
                <IconButton
                    iconName={on ? "chevron-right" : "chevron-left"}
                    size={STAGE_BUTTON}
                    iconSize={18}
                    color={FACE.fill}
                    iconColor={FACE.ink}
                    hitSlop={slopTo(STAGE_BUTTON)}
                    onPress={onToggle}
                    accessibilityLabel={on ? TEXTS.restore : TEXTS.enlarge}
                />
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** À cheval sur le bord gauche du volet, à mi-hauteur, devant la visio. */
    slot: {
        position: "absolute",
        left: -OUTER / 2,
        top: "50%",
        marginTop: -OUTER / 2,
        zIndex: 2,
    },
    button: {
        borderRadius: OUTER / 2,
        borderWidth: EDGE,
        borderColor: theme.colors[FACE.edge],
        ...shadowStyle(theme, "menu"),
    },
}))
